import { useMemo, useState } from "react";

import { useChartReadout } from "../../charts/pointSelection";
import { WagmiSpikeChart } from "../../charts/WagmiSpikeChart";
import { StyleSheet, Text, View } from "react-native";
import { useTranslation } from "react-i18next";

import {
  type GlucoseReading,
  type GlucoseUnit,
  type NutritionEntry,
  type Point,
  cadenceMs,
  freshness,
  fromCanonicalMgdl,
  glucoseNumberFormat,
  formatDayMonthTime,
} from "@heraion/shared";

import type { UiTheme } from "../../theme";
import { makeSettingsCardStyles } from "../settingsCardStyles";
import { EmptyState } from "../../primitives/EmptyState";
import { InfoPill, InfoPillBody } from "../../primitives/InfoPill";
import { StaleBadge } from "../../primitives/StaleBadge";
import { useDateLocale } from "../../primitives/DateFormatContext";

// Metabolic card (slice 3c): surfaces the glucose + nutrition the Health
// Connect merge imported, so the carbs↔glucose↔training picture is visible in
// JL. First overlay: a glucose trend + latest reading + recent carbs.
//
// Lifted to @heraion/ui (S3, 2026-06-29): the glucose chart is the shared
// interactive LineChart (neutral line + an orange "now" dot, tap-a-point
// value). The metabolic rows come in as props (mobile passes useMetabolic +
// the profile glucose unit; the web console passes useMetabolicWeb) so the
// same card renders on both. Reduced-vision: large current values, one chart.
//
// HM-1 (2026-07-15): glucose and nutrition each answer for THEMSELVES. `hasData`
// used to be `glucose.length > 0 || nutrition.length > 0`, so one logged meal
// kept the whole card alive with zero glucose behind it and the reading row just
// silently vanished. From ~25-07 Antenore is over a month without a CGM: absent
// glucose is the NORMAL state here, and it has to LOOK absent.

function sameDay(aISO: string, bMs: number): boolean {
  return new Date(aISO).toDateString() === new Date(bMs).toDateString();
}

// Universal unit suffix — kept literal (not i18n) to match QuickGlucoseCard.
const unitLabel = (u: GlucoseUnit): string => (u === "mgdl" ? "mg/dL" : "mmol/L");

// Canonical mg/dL display target band (70–180 mg/dL) → the chosen unit.
const TARGET_LO_MGDL = 70;
const TARGET_HI_MGDL = 180;

type Props = {
  glucose: GlucoseReading[];
  nutrition: NutritionEntry[];
  glucoseUnit: GlucoseUnit;
  loading: boolean;
  locale: string;
  nowMs: number;
  theme: UiTheme;
};

export function MetabolicCard({
  glucose,
  nutrition,
  glucoseUnit: unit,
  loading,
  locale,
  nowMs,
  theme,
}: Props) {
  const { t } = useTranslation();
  const dateLocale = useDateLocale();
  const [helpOpen, setHelpOpen] = useState(false);
  const card = useMemo(() => makeSettingsCardStyles(theme), [theme]);
  const styles = useMemo(() => makeStyles(theme), [theme]);

  const points = useMemo<Point[]>(() => {
    // API is newest-first; chart wants chronological. Plot in the user's unit.
    return [...glucose].reverse().map((g) => ({
      x: Date.parse(g.takenAt),
      y: fromCanonicalMgdl(g.mgDl, unit),
      // A glucose point carries its time: many readings share a day.
      label: formatDayMonthTime(new Date(g.takenAt), dateLocale),
    }));
  }, [glucose, dateLocale, unit]);

  // Review 25/09: the shared rule for glucose digits, `glucoseNumberFormat`
  // on a value already in the display unit.
  const glucoseFormat = (y: number) =>
    `${glucoseNumberFormat(unit, locale).format(y)} ${unitLabel(unit)}`;
  // PG-64 fetta 3: the glucose row reads the tapped point, the latest by
  // default, like every chart header.
  const { selection, readout, readoutProps } = useChartReadout(points, glucoseFormat, true);

  const latest = glucose[0];
  const todayCarbs = useMemo(() => {
    let sum = 0;
    for (const n of nutrition)
      if (n.carbsG !== undefined && sameDay(n.takenAt, nowMs)) sum += n.carbsG;
    return Math.round(sum);
  }, [nutrition, nowMs]);

  // Per-metric, never shared: one carb entry must not vouch for the glucose
  // feed. `points` is already chronological, which is what freshness reads.
  const glucoseAge = useMemo(() => {
    const xs = points.map((p) => p.x);
    return { fresh: freshness(xs, nowMs), cadence: cadenceMs(xs) };
  }, [points, nowMs]);
  const showGlucoseAge = glucoseAge.fresh.known && glucoseAge.fresh.stale && latest;

  // One condition for the `?` and its body, mirroring the WagmiSpikeChart
  // gate further down. Kept hand-rolled (not PanelTitleRow): that always
  // shows its pill, and here the pill exists only when a chart does.
  const chartShown = points.length >= 2;

  return (
    <View style={card.card}>
      <View style={styles.headRow}>
        <Text style={styles.head}>
          {t("mobile.metabolic.title", { defaultValue: "Metabolic" })}
        </Text>
        {/* #129: the chart explains itself behind the `?`, like Progress.
            Only when the chart below renders (points.length mirrors the
            WagmiSpikeChart gate further down). */}
        {chartShown ? (
          <InfoPill
            theme={theme}
            open={helpOpen}
            onToggle={() => setHelpOpen((v) => !v)}
          />
        ) : null}
      </View>
      {helpOpen && chartShown ? (
        <InfoPillBody
          theme={theme}
          text={t("mobile.metabolic.help", {
            defaultValue:
              "Your glucose from Health Connect, with recent carbs below it. Tap a point on the chart to read its value and time.",
          })}
        />
      ) : null}

      {loading && glucose.length === 0 && nutrition.length === 0 ? (
        <Text style={card.cardHelp}>
          {t("mobile.metabolic.loading", { defaultValue: "Loading…" })}
        </Text>
      ) : (
        <>
          {glucose.length === 0 ? (
            <EmptyState
              theme={theme}
              icon="info"
              title={t("mobile.metabolic.glucoseEmpty", {
                defaultValue: "No glucose readings",
              })}
              sub={t("mobile.metabolic.empty", {
                defaultValue:
                  "Connect Health Connect and sync to see glucose and nutrition here.",
              })}
            />
          ) : (
            <>
              {latest ? (
                <View style={[styles.row, styles.readoutRow]}>
                  <Text style={styles.label}>
                    {t("mobile.metabolic.glucoseTitle", { defaultValue: "Glucose" })}
                  </Text>
                  <Text style={[styles.value, styles.readoutValue]} {...readoutProps}>
                    {readout}
                  </Text>
                </View>
              ) : null}

              {showGlucoseAge && latest ? (
                <View style={styles.ageRow}>
                  <StaleBadge
                    theme={theme}
                    atMs={Date.parse(latest.takenAt)}
                    cadenceMs={glucoseAge.cadence}
                  />
                </View>
              ) : null}

              {/* SPIKE PG-1 — wagmi al POSTO del nostro grafico, sua parola del
                  16/09: «puoi togliere la vecchia». Fatto: `LineChart.tsx` è
                  stato cancellato dal repo su questo stesso branch (i tre
                  callsite di produzione sono passati a <WagmiLineChart>) — si
                  recupera solo da `main`, se mai servisse. */}
              {chartShown ? (
                <WagmiSpikeChart
                  points={points}
                  theme={theme}
                  nowMs={nowMs}
                  yFormat={glucoseFormat}
                  selection={selection}
                />
              ) : null}

              <Text style={card.cardHelp}>
                {t("mobile.metabolic.targetBand", {
                  defaultValue: "Typical target range {{lo}}–{{hi}} {{unit}}.",
                  // Review 25/09: `lo`/`hi` finivano crudi in `{{lo}}`/
                  // `{{hi}}`, quindi sempre col punto decimale anche in
                  // IT/FR/DE/RU. Stessa regola dell'ultimo valore sopra,
                  // stessa `glucoseNumberFormat`, non una terza copia.
                  lo: glucoseNumberFormat(unit, locale).format(
                    fromCanonicalMgdl(TARGET_LO_MGDL, unit),
                  ),
                  hi: glucoseNumberFormat(unit, locale).format(
                    fromCanonicalMgdl(TARGET_HI_MGDL, unit),
                  ),
                  unit: unitLabel(unit),
                })}
              </Text>
            </>
          )}

          <View style={styles.row}>
            <Text style={styles.label}>
              {t("mobile.metabolic.carbsToday", { defaultValue: "Carbs today" })}
            </Text>
            <Text style={styles.value}>
              {todayCarbs}{" "}
              <Text style={styles.unit}>
                {t("mobile.metabolic.grams", { defaultValue: "g" })}
              </Text>
            </Text>
          </View>
        </>
      )}
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, typography: ty } = theme;
  return StyleSheet.create({
    headRow: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
    },
    head: { color: p.ink2, fontSize: ty.sizes.sm, fontWeight: "700" },
    row: {
      flexDirection: "row",
      alignItems: "baseline",
      justifyContent: "space-between",
      marginTop: s.s2,
    },
    // The readout never splits: when it does not fit beside the label it
    // wraps under it whole (WeekBarsCard's header, same rule).
    readoutRow: { flexWrap: "wrap", columnGap: s.s2 },
    readoutValue: { flexShrink: 1 },
    ageRow: { flexDirection: "row", marginTop: s.s1 },
    label: { color: p.ink2, fontSize: ty.sizes.base },
    value: {
      color: p.ink,
      fontSize: ty.sizes.lg,
      fontWeight: "700",
      fontVariant: ["tabular-nums"],
    },
    unit: { color: p.ink3, fontSize: ty.sizes.sm, fontWeight: "600" },
  });
}
